// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';

// `suppressErrors` is flipped on by sessionExpired.js once it has told the operator their
// session ended. After that every error toast is a downstream symptom of that one fact
// ("Could not create user", "Could not load orders"…); stacking them buries the one thing
// worth clicking.
export const TOAST_BUS = {
  listeners: [],
  dismissers: [],
  suppressErrors: false,
  push(t) {
    if (t.kind === 'error' && this.suppressErrors) return;
    this.listeners.forEach(l => l(t));
  },
  dismiss(id) { this.dismissers.forEach(l => l(id)); },
};

// The 4th argument is either an `action` { label, onClick }, the one thing the operator would do next
// (the toast then waits for them), or options (the venue module's, src/modules/venue-port):
//   { action, duration, placement: 'bottom-center', offset }
// 'bottom-center' centres the toast in the registered anchor (setToastAnchor below), just above the
// Canvas action bar, `offset` px from its bottom (default 64). With options an Undo action closes after
// 6s unless `duration` says otherwise. (Upstream's `variant: 'surface'` is now the only look; it is
// accepted and ignored.) Returns the toast's id, for dismissToast.
export const toast = (message, kind = 'success', detail, extra) => {
  const t = { id: Date.now() + Math.random(), message, kind, detail };
  if (extra && typeof extra.onClick === 'function') t.action = extra;
  else if (extra) {
    if (extra.action && typeof extra.action.onClick === 'function') t.action = { label: extra.action.label || 'Undo', onClick: extra.action.onClick };
    t.duration = typeof extra.duration === 'number' ? extra.duration : t.action ? 6000 : 4200;
    if (extra.placement === 'bottom-center') { t.placement = 'bottom-center'; t.offset = typeof extra.offset === 'number' ? extra.offset : 64; }
  }
  TOAST_BUS.push(t);
  return t.id;
};

/** Close a toast early (e.g. when the change it offers to undo is no longer undoable). */
export const dismissToast = (id) => TOAST_BUS.dismiss(id);

/** How long a toast stays, in ms, or null: an actionable toast without a duration waits for the operator. */
export const toastDuration = (t) => (typeof t.duration === 'number' ? t.duration : t.action ? null : 4200);

// Toast anchor: the element bottom-centre toasts sit in (the Venue Canvas stage). The last registered
// element wins; without one, bottom-centre toasts centre on the viewport.
const TOAST_ANCHOR = { el: null, listeners: new Set() };
/** Register `el` as the toast anchor. Returns the unregister function. */
export const setToastAnchor = (el) => {
  TOAST_ANCHOR.el = el;
  TOAST_ANCHOR.listeners.forEach(l => l(el));
  return () => { if (TOAST_ANCHOR.el === el) { TOAST_ANCHOR.el = null; TOAST_ANCHOR.listeners.forEach(l => l(null)); } };
};
/** Current anchor element and a subscription to changes (used by the toast container). */
export const toastAnchor = () => TOAST_ANCHOR.el;
export const onToastAnchor = (fn) => { TOAST_ANCHOR.listeners.add(fn); return () => TOAST_ANCHOR.listeners.delete(fn); };

// THE AUDIT TRAIL IS WRITTEN BY THE SERVER, not here.
//
// This kept an in-memory array in the browser: actor 'You', id 'USR-0001', IP '102.51.18.94' — all
// invented, never sent anywhere, gone on refresh, and shown beside real rows on the Audit page. The
// backend now records every operator write itself (services/auditCapture), with the real actor, so
// a console-side copy could only ever be a less truthful duplicate. Callers are left as they are;
// this records nothing.
export const AUDIT_BUS = { entries: [], listeners: [], notify() { this.listeners.forEach(l => l()); } };

export const logAudit = () => {};

export const useAuditLog = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force(x => x + 1);
    AUDIT_BUS.listeners.push(fn);
    return () => { AUDIT_BUS.listeners = AUDIT_BUS.listeners.filter(l => l !== fn); };
  }, []);
  return AUDIT_BUS.entries;
};


/**
 * COPY, AND SAY WHAT ACTUALLY HAPPENED.
 *
 * `navigator.clipboard.writeText` returns a PROMISE and rejects whenever the browser declines —
 * an insecure origin, a document that is not focused, a permission the user denied. Two shapes
 * were in use across this codebase and both report success unconditionally:
 *
 *   navigator.clipboard?.writeText(x); toast('Copied', 'success', x);
 *   try { navigator.clipboard?.writeText(x); toast('Copied', …) } catch { }
 *
 * The second looks defended and is not: a synchronous try/catch cannot catch an async rejection,
 * so it never fires. Either way the operator is told the thing is on their clipboard when it is
 * not — and they find out by pasting nothing into an email to the press.
 *
 * `?.` is kept because `navigator.clipboard` is genuinely undefined on an insecure origin, where
 * calling it would throw synchronously; awaiting `undefined` is fine and falls through to the
 * honest failure below.
 */
export async function copyToClipboard(text, { label = 'Copied', detail } = {}) {
  const value = String(text ?? '');
  try {
    try {
      await navigator.clipboard?.writeText(value);
      if (!navigator.clipboard) throw new Error('no clipboard');
    } catch {
      // THE PERMISSIONLESS PATH. An embedded browser, a permissions policy or an insecure origin
      // refuses the async API; a selected <textarea> and the copy command still work inside the
      // click that asked — and they are the only way when nothing copyable is shown on screen
      // (a whole event request).
      const box = document.createElement('textarea');
      box.value = value;
      box.setAttribute('readonly', '');
      box.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
      document.body.appendChild(box);
      box.select();
      const ok = document.execCommand('copy');
      box.remove();
      if (!ok) throw new Error('copy command refused');
    }
    toast(label, 'success', detail ?? value);
    return true;
  } catch {
    // Actionable, because there IS an action: every one of these call sites shows the value on
    // screen next to the button, so selecting it by hand is a real fallback, not a shrug.
    toast('Could not copy', 'warning', 'Your browser blocked the clipboard — select the text on screen and copy it manually.');
    return false;
  }
}
